import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import UserService from "../../../../rest/userService";
import { toast } from "react-toastify";

const generateLogin = (name) => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length < 2) return "";
    return `${parts[0]}.${parts[1]}`.toLowerCase();
};

const NewUserForm = ({ userToEdit, closeModal }) => {
    const [showPassword, setShowPassword] = useState(false);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: userToEdit?.name || "",
            login: userToEdit?.login || "",
            email: userToEdit?.email || "",
            password: "",
            role: userToEdit?.role || "ATTENDANT",
            client_code: sessionStorage.getItem('clientCode'), 
            login_code: sessionStorage.getItem('loginCode')
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .required("Nome é obrigatório")
                .test(
                    "two-words",
                    "O nome deve ser completo, formado por mais de uma palavra.",
                    (value) => value && value.trim().split(/\s+/).length >= 2
                )
                .max(24, "Nome deve ter no máximo 24 caracteres"),
            email: Yup.string()
                .required("Email é obrigatório")
                .email("Email inválido")
                .max(240, "Email deve ter no máximo 240 caracteres"),
            password: userToEdit
                ? Yup.string().notRequired()
                    .min(8, "Senha deve ter pelo menos 8 caracteres")
                    .matches(/[A-Z]/, "Senha deve ter pelo menos uma letra maiúscula")
                    .matches(/[a-z]/, "Senha deve ter pelo menos uma letra minúscula")
                    .matches(/[0-9]/, "Senha deve ter pelo menos um número")
                    .matches(/[!@#$%^&*(),.?":{}|<>]/, "Senha deve ter pelo menos um símbolo")
                : Yup.string()
                    .required("Senha é obrigatória")
                    .min(8, "Senha deve ter pelo menos 8 caracteres")
                    .matches(/[A-Z]/, "Senha deve ter pelo menos uma letra maiúscula")
                    .matches(/[a-z]/, "Senha deve ter pelo menos uma letra minúscula")
                    .matches(/[0-9]/, "Senha deve ter pelo menos um número")
                    .matches(/[!@#$%^&*(),.?":{}|<>]/, "Senha deve ter pelo menos um símbolo"),
            role: Yup.string().required("Role é obrigatório"),
        }),
        onSubmit: async (values) => {
            try {
                if (userToEdit) {
                    const updatedUser = {
                        name: values.name,
                        login: values.login,
                        email: values.email,
                        role: values.role,
                    };
                    if (values.password) {
                        updatedUser.password = values.password;
                    }
                    await UserService.updateUser(userToEdit.login, updatedUser);
                    toast.success("Usuário atualizado com sucesso!");
                } else {
                    await UserService.createUser(values);
                    toast.success("Usuário criado com sucesso!");
                }
                closeModal();
                window.location.reload(); //TODO: substituir por STOMP depois
            } catch (err) {
                console.error(err);
                toast.error("Erro ao salvar usuário");
            }
        },
    });

    useEffect(() => {
        if (!userToEdit) {
            formik.setFieldValue("login", generateLogin(formik.values.name));
        }
    }, [formik.values.name, userToEdit]);

    return (
        <form
            id="newUserForm"
            onSubmit={formik.handleSubmit}
            className="flex flex-col gap-4"
        >
            <div>
                <label className="text-custom_gray_font">Nome</label>
                <input
                    type="text"
                    name="name"
                    className="w-full px-4 py-2 border rounded-xl mt-1"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                />
                {formik.touched.name && formik.errors.name && (
                    <p className="text-red-500 text-sm mt-1">{formik.errors.name}</p>
                )}
            </div>

            <div>
                <label className="text-custom_gray_font">Login</label>
                <input
                    type="text"
                    name="login"
                    className="w-full px-4 py-2 border rounded-xl mt-1 bg-gray-100"
                    value={formik.values.login}
                    readOnly
                />
            </div>

            <div>
                <label className="text-custom_gray_font">Email</label>
                <input
                    type="email"
                    name="email"
                    className="w-full px-4 py-2 border rounded-xl mt-1"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                />
                {formik.touched.email && formik.errors.email && (
                    <p className="text-red-500 text-sm mt-1">{formik.errors.email}</p>
                )}
            </div>

            {!userToEdit && (
                <div className="relative">
                    <label className="text-custom_gray_font">Senha</label>
                    <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className="w-full px-4 py-2 border rounded-xl mt-1"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-9 text-gray-500"
                    >
                        {showPassword ? "Ocultar" : "Mostrar"}
                    </button>
                    {formik.touched.password && formik.errors.password && (
                        <p className="text-red-500 text-sm mt-1">{formik.errors.password}</p>
                    )}
                </div>
            )}

            <div>
                <label className="text-custom_gray_font">Role</label>
                <select
                    name="role"
                    className="w-full px-4 py-2 border rounded-xl mt-1"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.role}
                >
                    <option value="ADMIN">Administrador</option>
                    <option value="CASHIER">Caixa</option>
                    <option value="ATTENDANT">Atendente</option>
                </select>
                {formik.touched.role && formik.errors.role && (
                    <p className="text-red-500 text-sm mt-1">{formik.errors.role}</p>
                )}
            </div>
        </form>
    );
};

export default NewUserForm;
